import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { useTranslation } from '@shared/core';
import { StripeElements } from '@shared/core/stripe';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';
import { useCashFundGuestController } from '../CashFundGuest.controller';
import { CashFundGuestBody } from './CashFundGuestBody';

type CashFundGuestContentProps = Readonly<{
  readonly fundId: string;
  readonly event: GuestSiteEvent;
}>;

type MessageStateProps = Readonly<{
  readonly title: string;
  readonly subtitle?: string;
}>;

const MessageState: React.FC<MessageStateProps> = ({ title, subtitle }) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    gap={4}
    paddingX={6}
    paddingY={6}
    backgroundColor="white"
    borderRadius="12px"
  >
    <TextV2
      typographyVariant="hed6"
      color="mono14"
      textAlign="center"
    >
      {title}
    </TextV2>
    {subtitle && (
      <TextV2
        typographyVariant="body1"
        color="mono12"
        textAlign="center"
      >
        {subtitle}
      </TextV2>
    )}
  </Flex>
);

export const CashFundGuestContent: React.FC<CashFundGuestContentProps> = ({ fundId, event }) => {
  const { tq } = useTranslation('cashFundsGuest');
  const controllerData = useCashFundGuestController({ fundId });
  const { fund, loading, error } = controllerData;

  const stripeOptions = useMemo(() => {
    if (!fund?.cashFund) return null;

    const currency = fund.cashFund.currencyCode.toLowerCase();
    const amount = fund.cashFund.fixedContributionAmount?.valueInMinorUnits || 100;

    return {
      mode: 'payment' as const,
      currency,
      amount,
      appearance: { variables: { borderRadius: '25px' } },
    };
  }, [fund?.cashFund]);

  if (loading) {
    return <JoyLogoLoader loaderKey="cashfund-guest-content" />;
  }

  if (error) {
    return <MessageState title={tq`Unable to load fund information`} />;
  }

  if (!fund || !stripeOptions) {
    return <MessageState title={tq`Fund not found`} />;
  }

  if (fund.cashFund?.archived) {
    return (
      <MessageState
        title={tq`This cash fund is no longer active.`}
        subtitle={tq`Please contact the host for more details.`}
      />
    );
  }

  return (
    <StripeElements options={stripeOptions}>
      <CashFundGuestBody
        fundId={fundId}
        event={event}
        controllerData={controllerData}
      />
    </StripeElements>
  );
};
